import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useDomainTransitionContext } from '@app/context/DomainTransitionContext';

/**
 * Custom hook to manage loading state during domain transitions
 */
export const useDomainTransition = () => {
	const { isTransitioning, startTransition, endTransition } = useDomainTransitionContext();
	const location = useLocation();
	const previousDomainRef = useRef<string | null>(null);

	const getCurrentDomain = (pathname: string): string | null => {
		if (pathname.includes('/rateshopper/')) return 'rateshopper';
		if (pathname.includes('/review-manager/')) return 'review-manager';
		if (pathname.includes('/rms/')) return 'rms';
		if (pathname.includes('/bi/')) return 'bi';
		if (pathname.includes('/settings/')) return 'settings';
		return null;
	};

	useEffect(() => {
		const currentDomain = getCurrentDomain(location.pathname);
		const previousDomain = previousDomainRef.current;

		// Only trigger transition if we're switching between different domains
		if (currentDomain && currentDomain !== previousDomain && previousDomain !== null) {
			startTransition();
			
			// Always end transition after timeout, regardless of domain
			const timer = setTimeout(() => {
				endTransition();
			}, 800); // Increased to 800ms to ensure settings domain loads properly

			// Update the previous domain reference
			previousDomainRef.current = currentDomain;

			return () => clearTimeout(timer);
		}

		// Update the previous domain reference for first load or non-domain pages
		previousDomainRef.current = currentDomain;
		
		return () => {};
	}, [location.pathname, startTransition, endTransition]);

	return {
		isTransitioning,
		startTransition,
		endTransition
	};
};

export default useDomainTransition;
